import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Alert,
  LinearProgress
} from '@mui/material';

const SubscriptionStatus = ({ user }) => {
  const getStatusInfo = () => {
    if (!user) return { color: 'default', label: 'Chargement...', severity: 'info' };

    const { subscriptionStatus, trialStatus, subscriptionEndDate } = user;

    // Si l'utilisateur est en trial
    if (trialStatus) {
      return {
        color: 'warning',
        label: 'Période d\'essai',
        severity: 'warning',
        message: 'Vous êtes actuellement en période d\'essai gratuite. Passez à un abonnement payant pour continuer à utiliser toutes les fonctionnalités.'
      };
    }

    // Si l'abonnement est actif
    if (subscriptionStatus === 'active') {
      const endDate = subscriptionEndDate?.toDate ? subscriptionEndDate.toDate() : new Date(subscriptionEndDate);
      const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
      
      return {
        color: 'success',
        label: '✅ Abonnement actif',
        severity: 'success',
        message: `Votre abonnement est actif jusqu'au ${endDate.toLocaleDateString('fr-FR')} (${daysLeft} jours restants).`
      };
    }

    // Si l'abonnement a expiré
    if (subscriptionStatus === 'expired') {
      return {
        color: 'error',
        label: '❌ Abonnement expiré',
        severity: 'error',
        message: 'Votre abonnement a expiré. Renouvelez-le pour continuer à utiliser toutes les fonctionnalités.'
      };
    }

    // Si le paiement a échoué
    if (subscriptionStatus === 'payment_failed') {
      return {
        color: 'error',
        label: '⚠️ Problème de paiement',
        severity: 'error',
        message: 'Le dernier paiement a échoué. Veuillez mettre à jour vos informations de paiement.'
      };
    }

    // Statut par défaut (nouveau utilisateur)
    return {
      color: 'default',
      label: '🆕 Nouveau compte',
      severity: 'info',
      message: 'Bienvenue ! Choisissez un abonnement pour commencer à utiliser toutes les fonctionnalités.'
    };
  };

  const statusInfo = getStatusInfo();

  const getProgressValue = () => {
    if (!user || user.trialStatus || !user.subscriptionEndDate) return 0;
    
    const startDate = user.subscriptionStartDate?.toDate ? user.subscriptionStartDate.toDate() : new Date();
    const endDate = user.subscriptionEndDate?.toDate ? user.subscriptionEndDate.toDate() : new Date();
    const now = new Date();
    
    const totalDuration = endDate - startDate;
    const elapsed = now - startDate;
    
    return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Chip
          label={statusInfo.label}
          color={statusInfo.color}
          variant="filled"
          sx={{ fontWeight: 'bold' }}
          onClick={() => {}} // Prevent onClick errors
        />
        
        {user?.subscriptionType && (
          <Chip
            label={user.subscriptionType === 'annual' ? '📅 Annuel' : '📆 Mensuel'}
            variant="outlined"
            size="small"
            onClick={() => {}} // Prevent onClick errors
          />
        )}
      </Box>

      <Alert severity={statusInfo.severity} sx={{ mb: 2 }}>
        <Typography variant="body2">
          {statusInfo.message}
        </Typography>
      </Alert>

      {/* Barre de progression pour les abonnements actifs */}
      {user?.subscriptionStatus === 'active' && user?.subscriptionEndDate && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Progression de l'abonnement
          </Typography>
          <LinearProgress
            variant="determinate"
            value={getProgressValue()}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#f5f5f5',
              '& .MuiLinearProgress-bar': {
                backgroundColor: statusInfo.color === 'success' ? '#4caf50' : '#ff9800'
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {Math.round(getProgressValue())}% de la période écoulée
          </Typography>
        </Box>
      )}

    </Box>
  );
};

export default SubscriptionStatus;